import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType, } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction, } from '@ngrx/router-store';
import {Action, select, Store, } from '@ngrx/store';
import { Observable, of, zip, } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom, } from 'rxjs/operators';
import {
  loadEntity,
  loadEntityComplete,
  loadEntityFailure,
  loadFormatGroup,
  loadFormatGroupByIdentifierFailure,
  loadFormatGroupByRecordIdFailure,
  loadFormatGroupComplete,
  loadFormatGroupFailure,
} from '../../../entity/actions/full-entity.actions';
import { Concept, Entity, EntityTypes, FormatGroup, } from '../../../entity/models/entity';
import { FullEntityService } from '../../../entity/services/full-entity.service';
import { MetadataInput } from '../../../jsonld/models/full-record';
import { MetaData } from '../../../models/MetaData';
import { MetaTagLoaderService } from '../../../services/meta-tag-loader.service';
import {
  loadAllShowcases,
  loadAllShowcasesComplete,
  loadAllShowcasesFailure,
  loadMoreSingleShowcase,
  loadMoreSingleShowcaseComplete,
  loadMoreSingleShowcaseFailure,
  loadSingleShowcase,
  loadSingleShowcaseComplete,
  loadSingleShowcaseFailure,
} from '../actions/showcases.actions';
import { HitRelatedResource, ShowcaseData, SHOWCASES_PARAMS, ShowcasesResponse, ShowcasesTypes, } from '../models/showcases';
import { FullCardState } from '../reducers/full-card.reducer';
import { selectTabPageNum } from '../reducers/showcases.reducer';
import { ShowcasesService } from '../services/showcases.service';
import { setJsonld } from '../../../jsonld/actions/jsonld.actions';

@Injectable()
export class ShowcasesEffects {
  public cardNavigation$ = createEffect(() => this.actions$.pipe(
    ofType(ROUTER_NAVIGATION),
    map((action: RouterNavigationAction) => action.payload),
    filter((payload) => {
      const { url } = payload.event;
      return /^\/search\/card(\W|)/.test(url);
    })), { dispatch: false });

  public loadFullPage$ = createEffect(() => this.actions$.pipe(
    ofType(loadEntity, loadFormatGroup),
    withLatestFrom(this.cardNavigation$),
    switchMap(([{entity}, router]) => {
      const { id, entityType, recordId, isbn, upc, issn, controlNumber } = entity;
      let getCardData$: Observable<Entity | Concept | FormatGroup>;
      const queryParams = router.routerState.root.queryParams;

      if (recordId) {
        getCardData$ = this.fullCardService.getFormatGroupByRecordId(recordId);
      } else if (isbn) {
        getCardData$ = this.fullCardService.getFormatGroupByIsbn(isbn);
      } else if (upc) {
        getCardData$ = this.fullCardService.getFormatGroupByUpc(upc);
      } else if (issn) {
        getCardData$ = this.fullCardService.getFormatGroupByIssn(issn);
      } else if (controlNumber) {
        getCardData$ = this.fullCardService.getFormatGroupByControlNumber(controlNumber);
      } else {
        getCardData$ = this.fullCardService.getCardItemById(id, entityType, queryParams);
      }

      return getCardData$.pipe(
        switchMap( (response) => {
          this.metaTagLoaderService.setMetas(response as MetaData);
          const { id, entityType } = response;
          const { fullRecord, materialTabs, language, publicationDate } = response as FormatGroup;
          const metadataInput: MetadataInput = { fullRecord, materialTabs, language, publicationDate, recordId };
          const showcase = router.routerState.root.queryParams.showcase;
          const rapido = router.routerState.root.queryParams.rapido;
          const targetType = (entityType === EntityTypes.INSTANCE) ? EntityTypes.INSTANCE : EntityTypes.FORMAT_GROUP;
          const actions: Action[] = [];
          const loadCompleteAction = entityType === EntityTypes.FORMAT_GROUP ?
            loadFormatGroupComplete({
              entity: {
                fgEntity: (response as FormatGroup)
              }
            }) :
            loadEntityComplete({entity: response as Entity});
          actions.push(loadCompleteAction);

          if (showcase) {
            actions.push(loadSingleShowcase({ id, entityType, targetType }));
          }
          if (!showcase && !rapido) {
            actions.push(loadAllShowcases({ id, entityType, targetType }));
          }

          if (fullRecord) {
            actions.push(setJsonld({ metadataInput }));
          }

          return actions;
        }),
        catchError((error) => {
          this.router.navigate(['/404']);
          if (entityType === EntityTypes.FORMAT_GROUP) {
            return of(loadFormatGroupFailure({error}));
          }
          if (recordId) {
            return of(loadFormatGroupByRecordIdFailure({error}));
          }
          if (isbn || upc || issn || controlNumber) {
            return of(loadFormatGroupByIdentifierFailure({error}));
          }

          return of(loadEntityFailure({error}));
        }),
      );
    }),
  ));

  public allShowcasesLoad$ = createEffect(() => this.actions$.pipe(
    ofType(loadAllShowcases),
    switchMap(({id, entityType, targetType}) => {
      return this.showcases.getAllShowcases({ id, entityType, targetType }).pipe(
        map((response: ShowcasesResponse) => loadAllShowcasesComplete(response)),
        catchError((error) => {
          return of(loadAllShowcasesFailure(error));
        }),
      );
    }),
  ));

  public singleShowcaseLoad$ = createEffect(() => zip(
    this.cardNavigation$.pipe(
      filter((router) => router.routerState.root.queryParams.identifier),
    ),
    this.actions$.pipe(
      ofType(loadSingleShowcase),
    ),
  ).pipe(
    switchMap(([router, payload]) => {
      let response: Observable<ShowcaseData | HitRelatedResource>;
      const {id, entityType, targetType} = payload;
      const {showcase, identifier, showcaseId} = router.routerState.root.queryParams;
      const isMultiple = showcase === ShowcasesTypes.TOPICS || (showcase === ShowcasesTypes.CUSTOM && identifier === showcase);

      response = isMultiple ?
        this.showcases.getMultipleShowcase(showcase, {
          id,
          entityType,
          showcaseId,
          pageNum: 0,
          pageSize: SHOWCASES_PARAMS.TOPICS_PER_FULL_PAGE,
          targetType,
        }) :
        this.showcases.getSingleShowcase({
          id,
          entityType,
          showcase,
          identifier,
          showcaseId,
          pageNum: 0,
          pageSize: SHOWCASES_PARAMS.SINGLE_SHOWCASE_SEE_ALL,
          targetType,
        });
      return response.pipe(
        map((response) => loadSingleShowcaseComplete({data: response, showcase, identifier, isMultiple})),
        catchError((error) => {
          return of(loadSingleShowcaseFailure(error));
        }),
      );
    }),
  ));

  public singleShowcaseLoadMore$ = createEffect(() => this.actions$.pipe(
    ofType(loadMoreSingleShowcase),
    withLatestFrom(this.store.pipe(select(selectTabPageNum))),
    switchMap(([payload, pageNumState]) => {
      let response: Observable<ShowcaseData | HitRelatedResource>;
      const { id, entityType, showcase, showcaseId, identifier, pageSize } = payload;
      const targetType = (entityType === EntityTypes.INSTANCE) ? EntityTypes.INSTANCE : EntityTypes.FORMAT_GROUP;
      const isMultiple = showcase === ShowcasesTypes.TOPICS || (showcase === ShowcasesTypes.CUSTOM && identifier === showcase);
      response = isMultiple ?
        this.showcases.getMultipleShowcase(showcase, {
          id,
          entityType,
          showcaseId,
          pageNum: pageNumState[showcase],
          pageSize: SHOWCASES_PARAMS.TOPICS_PER_LOAD_MORE,
          targetType,
        }) :
        this.showcases.getSingleShowcase({
          id,
          entityType,
          showcase,
          showcaseId,
          identifier,
          pageNum: pageNumState[showcase],
          pageSize: pageSize || SHOWCASES_PARAMS.SINGLE_SHOWCASE_LOAD_MORE,
          targetType,
        });
      return response.pipe(
        map((response) => {
          return loadMoreSingleShowcaseComplete({ data: response, showcase, isMultiple });
        }),
        catchError((error) => {
          return of(loadMoreSingleShowcaseFailure(error));
        }),
      );
    }),
  ));

  constructor(
    private readonly actions$: Actions,
    private readonly fullCardService: FullEntityService,
    private readonly metaTagLoaderService: MetaTagLoaderService,
    private readonly store: Store<FullCardState>,
    private readonly router: Router,
    private showcases: ShowcasesService,
  ) { }
}
